/**
 * `createApiPoliciesSource` — the signed-in tenancy's ENFORCED visibility policy
 * ("what are they allowed to choose?", `@authe/fe-commons/attribution` `policy.ts`).
 *
 * Reads management-api `GET /policies` (canonical `/policies`, rewritten onto the
 * `.management.` zone by `RESOURCE_REWRITES`), which relays client-config-api's
 * resolution for the caller's OWN customer / org / tenant from the verified
 * token — the same shape and the same reason as `./featuresSource`.
 *
 * `cacheTtlMs: 0` for the same reason too: the path carries no tenancy, so the
 * ApiClient's URL-keyed cache would serve one tenancy's policy to the next. The
 * caller (`app/enforcedPolicy`) caches under a key that does carry it.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { enforcedPolicyFromWire, type EnforcedPolicy } from "@authe/fe-commons/attribution";

export const POLICIES_PATH = "/policies";

export interface PoliciesSource {
  /** The effective policy. Throws `ApiError` on a refused read. */
  getPolicies(signal?: AbortSignal): Promise<EnforcedPolicy>;
}

export function createApiPoliciesSource(client: ApiClient): PoliciesSource {
  return {
    async getPolicies(signal?: AbortSignal): Promise<EnforcedPolicy> {
      const res = await client.get<unknown>(POLICIES_PATH, { cacheTtlMs: 0, signal });
      return enforcedPolicyFromWire(unwrapData(res));
    },
  };
}
